// ============================================================
// TATHYA DMS - Database Grid View
// Version: 1.8.0
// Date: 2026-04-22
// Change (v1.8): PDF is now a first-class column type. The
//   dedicated row-level "PDF" column at the end of the grid
//   (plus its 📎/📄/✕ buttons, its global file input, and the
//   four handlePdf* handlers) has been removed. PDF cells now
//   render inline within the normal columns.map loop via the
//   new <PdfCell> component — which means:
//     * multiple PDF columns per database are supported
//     * PDF columns participate in drag-to-reorder like text/
//       number/date columns (closes the reorder bug)
//     * PDF cells are addressable per (row, column) with their
//       own upload/view/delete
//   Drag-and-drop upload is supported on each PDF cell.
// Change (v1.7): Date cells use native date picker (type=date).
//         Values stored as DD/MM/YYYY. toISO/fromISO helpers
//         convert for the picker. Existing mixed-format DB data
//         was normalised to DD/MM/YYYY before this change.
// Change (v1.6): Column drag-to-reorder (HTML5 native DnD)
// Change (v1.5): Blank-row ghost UX + backend validation
// Change (v1.4): Totals row (Clause 7.1.2)
// Change (v1.3): Multi-term AND search (Clause 1)
// Change (v1.2): Formula columns recalculate on cell edit
// Change (v1.1): Right-click cell → Action History popover (Clause 5.3)
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDatabase, getRows, addRow, updateRow,
  deleteRow, generateLink, revokeLink,
  uploadPdfToCell, deletePdfFromCell, getPdfCellUrl,
  getCellHistory, reorderColumns, renameDatabase
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ── Date helpers ──────────────────────────────────────────────
// We store and display dates as DD-Mon-YYYY (e.g. 01-Sep-2025).
// The native <input type="date"> works exclusively with YYYY-MM-DD
// for its value attribute. These helpers convert between the two.

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun',
                'Jul','Aug','Sep','Oct','Nov','Dec'];

// DD-Mon-YYYY → YYYY-MM-DD. Returns '' for empty/invalid input.
const toISO = (ddMonyyyy) => {
  if (!ddMonyyyy || typeof ddMonyyyy !== 'string') return '';
  const parts = ddMonyyyy.split('-');
  if (parts.length !== 3) return '';
  const [dd, mon, yyyy] = parts;
  if (!dd || !mon || !yyyy || yyyy.length !== 4) return '';
  const mm = MONTHS.findIndex(m => m.toLowerCase() === mon.toLowerCase()) + 1;
  if (mm === 0) return '';
  return `${yyyy}-${String(mm).padStart(2,'0')}-${dd.padStart(2,'0')}`;
};

// YYYY-MM-DD → DD-Mon-YYYY. Returns '' for empty/invalid input.
const fromISO = (yyyymmdd) => {
  if (!yyyymmdd || typeof yyyymmdd !== 'string') return '';
  const parts = yyyymmdd.split('-');
  if (parts.length !== 3) return '';
  const [yyyy, mm, dd] = parts;
  if (!yyyy || !mm || !dd) return '';
  const mon = MONTHS[parseInt(mm, 10) - 1];
  if (!mon) return '';
  return `${dd.padStart(2,'0')}-${mon}-${yyyy}`;
};

// Parse a column's allowed_values (JSON array stored as TEXT).
// Returns an array of strings, or null if the column has no
// dropdown restriction. Used by the cell editor to decide whether
// to render <select> or <input>.
const getAllowedValues = (col) => {
  if (!col || col.data_type !== 'text' || !col.allowed_values) return null;
  try {
    const parsed = Array.isArray(col.allowed_values)
      ? col.allowed_values
      : JSON.parse(col.allowed_values);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.map(v => String(v));
  } catch { return null; }
};

// Multi-term matcher (CEO Clause 1). Space-separates the query into
// terms; a row passes when EVERY term is found in SOME cell (AND
// across terms, OR across cells). E.g. "priya mumbai" matches a row
// that has "Priya" in one cell and "Mumbai" in another.
// Same logic duplicated in PublicView.js — keep in sync if semantics change.
const matchesSearch = (row, search) => {
  if (!search || !search.trim()) return true;
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  return terms.every(term =>
    row.DbCells?.some(cell =>
      (cell?.value ?? '').toString().toLowerCase().includes(term)
    )
  );
};

// Which column types contribute to the totals row (Clause 7.1.2
// style summation). Percentage is intentionally excluded — summing
// GST percentages has no useful meaning.
const SUMMABLE_TYPES = ['currency', 'number', 'formula'];

// Parse a raw cell value into a number for summation. Tolerates
// commas and stray whitespace; returns null for empty/error values
// so they're excluded from the sum rather than skewing it to 0.
const toNumeric = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s === '#ERR') return null;
  // Reject time/hour shaped values like "2:03" or "14:30" — parseFloat
  // would silently return the hour part (2 or 14), skewing the sum.
  if (/^-?\d+:\d{2}(\s*(AM|PM))?$/i.test(s)) return null;
  const cleaned = s.replace(/,/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};

// Format a sum for display: two decimals, comma-grouped thousands.
// 537570 -> "537,570.00". Null input -> empty string.
const formatSum = (n) => {
  if (n === null || n === undefined) return '';
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Parse a time/hour string (including negative like -2:15) to minutes.
// Returns null for unparseable values.
const toMinutes = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (!s || s === '#ERR') return null;
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  const parts = body.split(':');
  if (parts.length !== 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return (h * 60 + m) * (neg ? -1 : 1);
};

// Format total minutes as H:MM (handles negative).
const formatMinuteSum = (mins) => {
  const neg = mins < 0;
  const abs = Math.abs(Math.round(mins));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return (neg ? '-' : '') + h + ':' + String(m).padStart(2, '0');
};

// Compute totals for the given rows against the given columns.
// Returns { [columnId]: number|string } for SUMMABLE_TYPES columns only.
// Time/hour formula columns are summed as minutes and stored as H:MM string.
// Rows with invalid values for a column are skipped for that column.
const computeTotals = (rows, columns) => {
  const totals = {};
  const summable = columns.filter(c => SUMMABLE_TYPES.includes(c.data_type));
  for (const col of summable) {
    // Detect if this formula column contains time/hour values by checking
    // the first non-empty cell value.
    let isTimeLike = false;
    if (col.data_type === 'formula') {
      for (const row of rows) {
        const cell = row.DbCells?.find(c => c.column_id === col.id);
        const v = cell?.value;
        if (v && /^-?\d+:\d{2}$/.test(String(v).trim())) { isTimeLike = true; break; }
      }
    }
    if (isTimeLike) {
      let sum = 0;
      let any = false;
      for (const row of rows) {
        const cell = row.DbCells?.find(c => c.column_id === col.id);
        const m = toMinutes(cell?.value);
        if (m !== null) { sum += m; any = true; }
      }
      totals[col.id] = any ? { type: 'time', value: sum } : null;
    } else {
      let sum = 0;
      let any = false;
      for (const row of rows) {
        const cell = row.DbCells?.find(c => c.column_id === col.id);
        const n = toNumeric(cell?.value);
        if (n !== null) { sum += n; any = true; }
      }
      totals[col.id] = any ? sum : null;
    }
  }
  return totals;
};

// ── <PdfCell> — per-cell PDF upload / view / delete ──────────
// One instance rendered per PDF cell in the grid. Three states:
//   - empty:       shows "+ Upload PDF" or drop zone
//   - populated:   shows filename as link + delete (×) button
//   - uploading:   shows "Uploading…"
// All three entry points (click→filepicker, drag-drop, replace)
// share one handler so error handling is centralised. Size and
// type validation is deferred to the backend — which runs the
// same rules that applied to the old single-PDF upload (MIME =
// application/pdf, 50 MB ceiling, 250 KB × page count). We only
// do a cheap client-side MIME check here to avoid round-tripping
// obvious non-PDFs.
const PdfCell = ({
  orgSlug, databaseId, rowId, columnId,
  file,               // { id, original_name, file_size } or null
  canUpload,
  canDelete,
  onChange            // invoked after successful upload/delete so
                      // the grid can refetch
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.type !== 'application/pdf') {
      setErrorMsg('Only PDF files are allowed');
      return;
    }
    setIsUploading(true);
    setErrorMsg(null);
    try {
      await uploadPdfToCell(orgSlug, databaseId, rowId, columnId, selectedFile);
      if (onChange) onChange();
    } catch (err) {
      // Backend returns shaped errors for the 250 KB × page rule
      // (data.details.rule). Prefer that message; fall back to
      // data.error, then generic.
      const data = err?.response?.data;
      const msg = data?.details?.rule || data?.error || err.message || 'Upload failed';
      setErrorMsg(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!file) return;
    if (!window.confirm(`Delete ${file.original_name}?`)) return;
    try {
      await deletePdfFromCell(orgSlug, databaseId, rowId, columnId);
      if (onChange) onChange();
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Delete failed');
    }
  };

  const onDragOver = (e) => {
    if (!canUpload) return;
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canUpload) return;
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const cellStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: file ? 'space-between' : 'center',
    padding: '2px 4px',
    minHeight: 24,
    border: isDragging ? '2px dashed #2E75B6' : '1px dashed transparent',
    borderRadius: 4,
    background: isDragging ? '#EBF3FB' : 'transparent',
    transition: 'background 120ms, border-color 120ms',
    cursor: canUpload && !file ? 'pointer' : 'default',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box'
  };

  if (isUploading) {
    return (
      <div style={cellStyle}>
        <span style={{ fontSize: 12, color: '#B7600A' }}>Uploading…</span>
      </div>
    );
  }

  if (file) {
    // The JWT-via-?token= route works here too: the browser
    // navigation lands on /api/files/.../... and the Files
    // router lifts the query token into the Authorization
    // header before auth runs (see files.js router.use).
    const token = localStorage.getItem('accessToken');
    const baseUrl = getPdfCellUrl(orgSlug, databaseId, rowId, columnId);
    const url = `${baseUrl}?token=${token}`;
    return (
      <div style={cellStyle}>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          title={file.original_name}
          onClick={(e) => e.stopPropagation()}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: canDelete ? 'calc(100% - 22px)' : '100%',
            color: '#2E75B6',
            textDecoration: 'none',
            fontSize: 18,
            lineHeight: 1
          }}
        >
          📄
        </a>
        {canDelete && (
          <button
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); handleDelete(); }}
            title="Delete PDF"
            style={{
              background: 'none', border: 'none',
              color: '#C0392B', cursor: 'pointer',
              fontSize: 14, padding: '0 4px', lineHeight: 1
            }}
          >✕</button>
        )}
        {errorMsg && (
          <div style={{ position: 'absolute', top: '100%', left: 0,
                        fontSize: 11, color: '#C0392B', zIndex: 10,
                        background: 'white', padding: '2px 4px',
                        border: '1px solid #C0392B', borderRadius: 3 }}>
            {errorMsg}
          </div>
        )}
      </div>
    );
  }

  // Empty cell — click or drop to upload.
  return (
    <div
      style={cellStyle}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={(e) => {
        e.stopPropagation();
        if (canUpload && inputRef.current) inputRef.current.click();
      }}
    >
      {canUpload ? (
        <span style={{ fontSize: 11, color: '#595959' }}>
          {isDragging ? 'Drop PDF here' : '📎 Upload PDF'}
        </span>
      ) : (
        <span style={{ fontSize: 11, color: '#aaa' }}>—</span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
      {errorMsg && (
        <div style={{ position: 'absolute', top: '100%', left: 0,
                      fontSize: 11, color: '#C0392B', zIndex: 10,
                      background: 'white', padding: '2px 4px',
                      border: '1px solid #C0392B', borderRadius: 3 }}>
          {errorMsg}
        </div>
      )}
    </div>
  );
};

const DatabaseView = () => {
  const { org_slug, db_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [database, setDatabase] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  // Inline validation message for the currently-edited cell.
  // Shown beneath the input. Cleared when the user keeps typing or
  // navigates away to a different cell. Today only used for time
  // columns in ghost-row mode (Apr 25); broader use later if needed.
  const [editError, setEditError] = useState('');
  const [copyMsg, setCopyMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const inputRef = useRef(null);
  const navigationRef = useRef(null); // tracks if we're navigating via keyboard
  const editValueRef = useRef('');

  // Cell Action History (right-click popover)
  const [history, setHistory] = useState(null);
  //   null | { anchorX, anchorY, rowId, colId, columnLabel,
  //            loading, events, isFormula, error }
  const popoverRef = useRef(null);

  // Column drag-to-reorder state. Owner/admin-only feature; the
  // flags are just UI book-keeping, the server is the source of
  // truth on the actual column order.
  const [draggedColId, setDraggedColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);

  // ── Rename-database (title click-to-edit) ────────────────────
  // Owner/admin only. Click the title → input appears. Enter saves,
  // Esc cancels. Empty or unchanged names are no-ops. Server responds
  // with the fresh database row (including owner) which replaces
  // state, so any downstream UI stays in sync.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef(null);

  // ── Fill-then-save state (Phase 2 of fill-then-save build) ──
  // These are only actively used when the current user has can_add
  // but not can_edit. For users with can_edit (owners, admins, or
  // granted users with can_edit), the ghost-row flow remains the
  // existing instant-save behavior and these values stay at their
  // defaults. See fillThenSaveMode below.
  //
  // ghostDraft   — colId → in-progress cell value, client-side only
  //                until the user clicks Save
  // ghostDirty   — true when the ghost row has at least one
  //                unsaved value (drives the confirmation modal
  //                and the beforeunload handler in later phases)
  // unsavedPrompt — null, or an object describing what the user
  //                was trying to do when they got blocked by the
  //                "Unsaved changes" modal (e.g. edit another row)
  // ghostSaveError — inline error shown on the ghost Save button
  //                after a failed POST (kept out of the global
  //                error banner so the user's draft stays visible)
  const [ghostDraft, setGhostDraft] = useState({});
  const [ghostDirty, setGhostDirty] = useState(false);
  const [unsavedPrompt, setUnsavedPrompt] = useState(null);
  const [ghostSaveError, setGhostSaveError] = useState('');

  const startRename = () => {
    if (!isOwner || !database) return;
    setRenameValue(database.name || '');
    setRenaming(true);
    setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 0);
  };

  const cancelRename = () => {
    setRenaming(false);
    setRenameValue('');
  };

  const commitRename = async () => {
    const next = renameValue.trim();
    if (!next || next === database.name) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      const res = await renameDatabase(org_slug, db_id, next);
      setDatabase(res.data);
      setRenaming(false);
      setRenameValue('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename database');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleRenameKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
  };

  const isOwner = Number(database?.owner_id) === Number(user?.id) ||
    ['platform_admin', 'super_admin'].includes(user?.role);

  // ── Derived: which write workflow applies to this user here ──
  // canEditInstantly  — owner, admin, or granted user with can_edit.
  //                    Falls back to true when my_permission is
  //                    missing from the payload (defensive: if the
  //                    server didn't tell us, don't block edits).
  // fillThenSaveMode  — user can add rows but NOT edit existing
  //                    rows. New rows must accumulate locally and
  //                    submit as a single POST. Existing rows are
  //                    not editable at all (UI should prevent the
  //                    cell click that would otherwise 403).
  // Both of these are used by later phases; Phase 2 only introduces
  // them so the rest of the component can branch on them safely.
  const canEditInstantly =
    isOwner || Boolean(database?.my_permission?.can_edit);
  const fillThenSaveMode =
    !isOwner
    && Boolean(database?.my_permission?.can_add)
    && !database?.my_permission?.can_edit;

  const loadData = useCallback(async () => {
    try {
      const [dbRes, rowsRes] = await Promise.all([
        getDatabase(org_slug, db_id),
        getRows(org_slug, db_id)
      ]);
      setDatabase(dbRes.data);
      setRows(rowsRes.data.rows || []);
    } catch (err) {
      setError('Failed to load: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [org_slug, db_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Fill-then-save: warn on tab-close with dirty ghost (Phase 6) ──
  // Only attach the listener when we actually have unsaved data in
  // fill-then-save mode. Browsers ignore the returnValue text and
  // show their own generic message ("Changes may not be saved"),
  // but they require e.returnValue to be set (even to empty string)
  // to trigger the dialog at all.
  useEffect(() => {
    if (!fillThenSaveMode || !ghostDirty) return;
    const onBefore = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [fillThenSaveMode, ghostDirty]);

  // Keep ref in sync with state
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  // Client-side mirror of backend evaluateFormula (middleware/validate.js).
  // Used only for ghost rows in fill-then-save mode — real rows have
  // formulas computed server-side via computeRowWithFormulas. Kept in
  // lockstep with backend: same regex, same rounding, same safety guard.
  // (Added Apr 24 — Item 6 fix: formula cells stayed blank during ghost
  // entry because there was no client-side evaluator.)
  // ── Time helpers (Apr 25) ────────────────────────────────
  // Mirror of backend (validate.js). MUST stay in sync — any change
  // here needs the same change there, and vice versa, so live
  // ghost-row formula previews match what the server stores on save.
  const parseTimeToMinutes = (raw) => {
    if (raw === null || raw === undefined) return null;
    const s = String(raw).trim().toLowerCase();
    if (!s) return null;
    let meridiem = null;
    let body = s;
    const mMatch = s.match(/\s*(am|pm)\s*$/);
    if (mMatch) { meridiem = mMatch[1]; body = s.slice(0, mMatch.index).trim(); }
    if (!/^\d{1,2}(:\d{1,2}(:\d{1,2})?)?$/.test(body)) return null;
    const parts = body.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    if (isNaN(hours) || isNaN(minutes)) return null;
    if (minutes < 0 || minutes > 59) return null;
    if (meridiem) {
      if (hours < 1 || hours > 12) return null;
      if (meridiem === 'am') hours = hours === 12 ? 0 : hours;
      else hours = hours === 12 ? 12 : hours + 12;
    } else {
      if (hours < 0 || hours > 23) return null;
    }
    return hours * 60 + minutes;
  };
  const isTimeValue = (v) => typeof v === 'string' && /^\d{1,2}:[0-5]\d$/.test(v.trim());

  // Format minutes-since-midnight as 24-hour HH:MM for storage.
  // Mirror of backend (validate.js).
  const formatMinutesToTime = (totalMinutes) => {
    const m = ((totalMinutes % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return String(h).padStart(2, '0') + ':' + String(mm).padStart(2, '0');
  };

  // Mirror of backend phone validation (validate.js line 164-170).
  // MUST stay in sync. Strips all non-digits and requires exactly 10.
  // So '98765 43210', '(987) 654-3210' all normalize to '9876543210'.
  // Note: '+91-9876543210' becomes '919876543210' (12 digits) and is
  // REJECTED — same as backend.
  const parseAndValidatePhone = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return { valid: true, value: '' }; // empty = clear cell
    const digits = s.replace(/\D/g, '');
    if (digits.length !== 10) {
      return { valid: false, error: 'Phone number must be exactly 10 digits' };
    }
    return { valid: true, value: digits };
  };

  // Mirror of backend email validation (validate.js line 172-178).
  // MUST stay in sync. Stores lowercased on success.
  const parseAndValidateEmail = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return { valid: true, value: '' }; // empty = clear cell
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(s)) {
      return { valid: false, error: 'Enter a valid email, e.g. name@company.com' };
    }
    return { valid: true, value: s.toLowerCase() };
  };

  // ── Hour (duration) helpers — Apr 28, mirror of backend.
  // 'hour' is a duration type with no upper bound. Stored as
  // canonical 'H:MM' (no zero-pad on hours). See validate.js for
  // the authoritative spec — these MUST stay in sync.
  const parseHourToMinutes = (raw) => {
    if (raw === null || raw === undefined) return null;
    const s = String(raw).trim();
    if (!s) return null;
    if (!/^\d+(:\d{1,2}(:\d{1,2})?)?$/.test(s)) return null;
    const parts = s.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    if (isNaN(hours) || isNaN(minutes)) return null;
    if (hours < 0) return null;
    if (minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
  };

  const formatMinutesToHour = (totalMinutes) => {
    if (totalMinutes === null || totalMinutes === undefined) return '';
    const m = Math.max(0, Math.round(Number(totalMinutes)));
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return h + ':' + String(mm).padStart(2, '0');
  };

  // Match a stored hour value. Hour values share H:MM shape with
  // time values, but allow hour > 23. Stored canonical form has no
  // zero-pad on hours, but be forgiving on the regex so legacy
  // entries like "01:58" still match.
  const isHourValue = (v) => typeof v === 'string' && /^\d+:[0-5]\d$/.test(v.trim());

  const parseAndValidateHour = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return { valid: true, value: '' };
    const mins = parseHourToMinutes(s);
    if (mins === null) {
      return {
        valid: false,
        error: 'Must be a valid duration in H:MM format (e.g. 1:58, 25:15)'
      };
    }
    return { valid: true, value: formatMinutesToHour(mins) };
  };

  // Format an hour value for display. Same as storage (H:MM) — no
  // 12-hour conversion (durations don't have AM/PM).
  const formatHourForDisplay = (v) => {
    if (!isHourValue(v)) return v;
    const mins = parseHourToMinutes(v);
    return mins == null ? v : formatMinutesToHour(mins);
  };

  // Format a stored 24-hour HH:MM as 12-hour h:mm AM/PM for display.
  // Empty / non-time inputs pass through unchanged so this is safe to
  // call on any cell value.
  const formatTimeForDisplay = (v) => {
    if (!isTimeValue(v)) return v;
    const mins = parseTimeToMinutes(v);
    if (mins == null) return v;
    const h24 = Math.floor(mins / 60);
    const mm = mins % 60;
    const meridiem = h24 < 12 ? 'AM' : 'PM';
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return h12 + ':' + String(mm).padStart(2, '0') + ' ' + meridiem;
  };

  // ── evaluateFormulaClient (Item 6, extended Apr 25 for time, Apr 28 for hour) ──
  // Local re-implementation of validate.js's evaluateFormula so ghost
  // rows show live formula results without round-tripping the server.
  // Three modes selected by referenced column types:
  //   - hour mode: every ref is type 'hour', operators are +/-.
  //     Operands are minutes, NO mod-24 wrap. Result formatted H:MM.
  //   - time mode: every ref is type 'time' (clock), operators +/-.
  //     Mod-24 wrap for cross-midnight subtraction.
  //   - numeric mode: anything else.
  // columnTypes is optional { label: data_type }. When omitted,
  // falls back to value-shape detection.
  const evaluateFormulaClient = (expression, cellsByLabel, columnTypes) => {
    try {
      if (!expression) return '';
      const matches = expression.match(/\[([^\]]+)\]/g) || [];

      const referenced = [];
      for (const m of matches) {
        const colName = m.slice(1, -1);
        const v = cellsByLabel[colName];
        const t = columnTypes ? columnTypes[colName] : undefined;
        referenced.push({ token: m, value: v, type: t });
      }
      const operatorsOK = !/[\*\/]/.test(expression);
      let mode = 'numeric';
      if (referenced.length > 0 && operatorsOK) {
        const allHour = referenced.every(r =>
          r.type === 'hour' || (r.type === undefined && isHourValue(r.value) && !isTimeValue(r.value))
        );
        const allTime = referenced.every(r =>
          r.type === 'time' || (r.type === undefined && isTimeValue(r.value))
        );
        if (allHour) mode = 'hour';
        else if (allTime) mode = 'time';
      }

      if (mode === 'hour') {
        let formula = expression;
        for (const r of referenced) {
          const mins = parseHourToMinutes(r.value);
          formula = formula.replace(r.token, String(mins == null ? 0 : mins));
        }
        if (!/^[\d\s\+\-\(\)]+$/.test(formula)) return '';
        // eslint-disable-next-line no-new-func
        const raw = Function('"use strict"; return (' + formula + ')')();
        if (isNaN(raw) || !isFinite(raw)) return '0:00';
        const rounded = Math.round(raw);
        if (rounded < 0) {
          return '-' + formatMinutesToHour(Math.abs(rounded));
        }
        return formatMinutesToHour(rounded);
      }

      if (mode === 'time') {
        let formula = expression;
        for (const r of referenced) {
          const mins = parseTimeToMinutes(r.value);
          formula = formula.replace(r.token, String(mins == null ? 0 : mins));
        }
        if (!/^[\d\s\+\-\(\)]+$/.test(formula)) return '#ERR';
        // eslint-disable-next-line no-new-func
        const raw = Function('"use strict"; return (' + formula + ')')();
        if (isNaN(raw) || !isFinite(raw)) return '#ERR';
        const rounded = Math.round(raw);
        if (rounded >= 1440) return '#ERR';
        const abs = Math.abs(rounded);
        const h = Math.floor(abs / 60);
        const mm = abs % 60;
        const sign = rounded < 0 ? '-' : '';
        return sign + h + ':' + String(mm).padStart(2, '0');
      }

      // Numeric path — unchanged
      let formula = expression;
      if (matches.length) {
        for (const match of matches) {
          const colName = match.slice(1, -1);
          const value = cellsByLabel[colName];
          if (value === undefined || value === '' || value === null) {
            formula = formula.replace(match, '0');
          } else {
            const num = parseFloat(value);
            formula = formula.replace(match, isNaN(num) ? '0' : num);
          }
        }
      }
      if (!/^[\d\s\+\-\*\/\(\)\.\,]+$/.test(formula)) return '';
      // eslint-disable-next-line no-new-func
      const result = Function('"use strict"; return (' + formula + ')')();
      if (isNaN(result) || !isFinite(result)) return '0';
      return String(Math.round(result * 100) / 100);
    } catch {
      return '';
    }
  };

  const getCellValue = (row, colId) => {
    // Fill-then-save ghost rows hold their user-entered values in
    // ghostDraft (keyed by colId) rather than in DbCells, because
    // nothing has been POSTed yet. Read ghostDraft first in that
    // case so the grid shows the cells the user has already filled
    // in as they tab around.
    if (row && isGhostRow(row.id) && fillThenSaveMode) {
      // Formula columns: evaluate against ghostDraft on the fly.
      // The user hasn't saved yet, so the server can't compute for us —
      // match backend semantics (percentage auto-divided by 100, same
      // expression parser, same rounding) so what the ghost shows is
      // what the saved row will show. See evaluateFormulaClient above.
      const cols = database?.DbColumns || [];
      const col = cols.find(c => c.id === colId);
      if (col && col.data_type === 'formula') {
        // Build cellsByLabel from non-formula cells only (formula
        // values get filled in below by topo-sort iteration).
        const cellsByLabel = {};
        for (const c of cols) {
          if (c.data_type === 'formula') continue;
          const raw = ghostDraft[c.id];
          if (raw === undefined || raw === null || raw === '') continue;
          if (c.data_type === 'percentage') {
            const n = parseFloat(raw);
            cellsByLabel[c.label] = isNaN(n) ? raw : (n / 100);
          } else {
            cellsByLabel[c.label] = raw;
          }
        }

        // ── Formula chaining (Apr 25 — Item 20) ─────────────
        // Resolve formulas in dependency order. Mirror of backend
        // resolveAllFormulas. Anything still unresolved at the
        // end of a no-progress pass is part of a cycle.
        const refsOf = (expr) => {
          const matches = (expr || '').match(/\[([^\]]+)\]/g) || [];
          return matches.map(m => m.slice(1, -1));
        };
        const formulaCols = cols.filter(c => c.data_type === 'formula');
        // Set of formula column labels — only formula refs need to
        // wait for resolution. Empty/missing data refs are safe to
        // substitute as 0 immediately, matching evaluateFormula's
        // long-standing forgiving behaviour.
        // (Apr 25 — fix on the Item 20 fix; previous predicate
        // wrongly stalled on empty data cells and emitted #CIRCULAR.)
        const formulaLabels = new Set(formulaCols.map(c => c.label));
        // Column-label -> data_type, passed into evaluateFormulaClient
        // so it can pick hour vs time arithmetic correctly even when
        // both share H:MM shape. (Apr 28 — hour type.)
        const columnTypes = {};
        cols.forEach(c => { columnTypes[c.label] = c.data_type; });
        const resolved = {};
        const resolvedFormulas = new Set();
        const unresolved = new Map();
        formulaCols.forEach(c => unresolved.set(c.label, c));

        let progress = true;
        while (progress && unresolved.size > 0) {
          progress = false;
          for (const [label, fc] of unresolved) {
            const refs = refsOf(fc.formula_expression);
            // Ready when every reference is either a non-formula
            // column (always treated as 0 if empty) or a formula
            // already resolved this pass.
            const ready = refs.every(r => !formulaLabels.has(r) || resolvedFormulas.has(r));
            if (!ready) continue;
            const v = evaluateFormulaClient(fc.formula_expression, cellsByLabel, columnTypes);
            resolved[label] = v;
            const n = parseFloat(v);
            cellsByLabel[label] = isNaN(n) ? v : n;
            resolvedFormulas.add(label);
            unresolved.delete(label);
            progress = true;
          }
        }
        // True cycles → #CIRCULAR for the survivors.
        for (const [label] of unresolved) {
          resolved[label] = '#CIRCULAR';
        }

        return resolved[col.label] !== undefined ? resolved[col.label] : '';
      }
      const v = ghostDraft[colId];
      return v === undefined || v === null ? '' : v;
    }
    const cell = row.DbCells?.find(c => c.column_id === colId);
    return cell?.value || '';
  };

  // Ghost row: clicking "+ Add Row" stages a blank row in local
  // state only. Not persisted until the user types a value and
  // commits. If they click away or press Esc without typing, the
  // ghost is silently removed. Prevents the "blank row" bug where
  // accidental clicks produced empty records.
  const isGhostRow = (id) => typeof id === 'string' && id.startsWith('ghost-');

  const handleAddRow = () => {
    const cols = database?.DbColumns || [];
    if (cols.length === 0) {
      alert('Add columns to this database before adding rows.');
      return;
    }
    // Fill-then-save guard (Phase 5): if a ghost row already has
    // unsaved data, prompt instead of silently wiping it. The user
    // picks Save / Discard / Cancel in the modal; the resulting
    // action resumes or aborts this Add Row request. We stash the
    // intent on unsavedPrompt so the modal knows what to do next.
    if (fillThenSaveMode && ghostDirty) {
      setUnsavedPrompt({ kind: 'add-new-row' });
      return;
    }
    performAddRow();
  };

  // Actual row creation, separated from the guard so the Save/Discard
  // resolution paths in the unsaved-changes modal can trigger it.
  const performAddRow = () => {
    const cols = database?.DbColumns || [];
    if (cols.length === 0) return;
    // Drop any existing ghost first — only ever one pending row at a time.
    setRows(prev => {
      const cleaned = prev.filter(r => !isGhostRow(r.id));
      const ghostId = `ghost-${Date.now()}`;
      const ghost = {
        id: ghostId,
        database_id: Number(db_id),
        DbCells: [],
        isGhost: true
      };
      return [...cleaned, ghost];
    });
    // Focus the first editable cell of the new ghost row next tick.
    // We use the column position to find the leftmost non-formula col.
    setTimeout(() => {
      const firstCol = cols.find(c => c.data_type !== 'formula') || cols[0];
      if (!firstCol) return;
      // Find the ghost's id from state by re-reading — setRows above is async.
      setRows(curr => {
        const ghost = curr.find(r => isGhostRow(r.id));
        if (ghost) startEdit(ghost.id, firstCol.id, '');
        return curr;
      });
    }, 0);
  };

  const dropGhostRows = () => {
    setRows(prev => prev.filter(r => !isGhostRow(r.id)));
  };

  const handleDeleteRow = async (rowId) => {
    if (isGhostRow(rowId)) {
      // Just drop from state — nothing on the server.
      setRows(prev => prev.filter(r => r.id !== rowId));
      return;
    }
    if (!window.confirm('Delete this row?')) return;
    try {
      await deleteRow(org_slug, db_id, rowId);
      setRows(prev => prev.filter(r => r.id !== rowId));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  // ── Fill-then-save: Save / Discard on the ghost row (Phase 4) ──
  // These handlers are only reachable when fillThenSaveMode is true
  // and a ghost row exists. The buttons themselves are rendered
  // conditionally in the action <td>. On a successful save the
  // ghost row is replaced with the real row returned by the server.
  const handleGhostSave = async () => {
    // Build the cells payload from the accumulated ghostDraft,
    // keeping only entries with a non-empty, non-whitespace value.
    // A new row with zero filled cells doesn't make sense, so we
    // require at least one non-empty cell. Date columns have already
    // been normalised to DD/MM/YYYY on each blur (see commitEdit).
    const cells = {};
    for (const [colId, v] of Object.entries(ghostDraft)) {
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s === '') continue;
      // Preserve the original (possibly typed-number) value, not
      // the trimmed string — trimming was only for the emptiness check.
      cells[colId] = v;
    }
    if (Object.keys(cells).length === 0) {
      setGhostSaveError('Please fill at least one cell before saving.');
      return;
    }

    setSaving(true);
    setGhostSaveError('');
    try {
      const res = await addRow(org_slug, db_id, { cells });
      const real = res?.data;
      if (real && real.id) {
        // Swap the ghost for the real row returned by the server.
        setRows(prev => prev.map(row =>
          isGhostRow(row.id) ? real : row
        ));
      } else {
        // Unexpected shape — drop the ghost so we don't leave a
        // stuck-looking row in the UI; user can re-add if needed.
        setRows(prev => prev.filter(r => !isGhostRow(r.id)));
      }
      setGhostDraft({});
      setGhostDirty(false);
    } catch (err) {
      // Keep the ghost on screen so the user's typing is not lost.
      // They can fix the problem and click Save again.
      //
      // The server returns {error: 'Validation failed', errors: [{column, error}, ...]}
      // for per-cell validation failures. Surface the per-column detail
      // so the user knows WHICH cell is wrong — a bare "Validation failed"
      // leaves them guessing. (Added Apr 24 — Item 2 fix.)
      const data = err.response?.data;
      let msg = data?.error || 'Failed to save row.';
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const lines = data.errors.map(e =>
          e.column ? `${e.column}: ${e.error}` : e.error
        );
        msg = `${msg}\n${lines.join('\n')}`;
      }
      setGhostSaveError(msg);
      // Also surface as a native popup so the user is prompted explicitly.
      // The inline error block stays visible too, as a readable record after
      // the popup is dismissed. (Apr 24 follow-up — Item 2.)
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleGhostDiscard = () => {
    // Drop the ghost row and reset all fill-then-save state.
    // No confirm() here — Phase 5 adds the "unsaved changes" modal
    // for accidental navigation; explicit Discard is already
    // the user's deliberate intent.
    setRows(prev => prev.filter(r => !isGhostRow(r.id)));
    setGhostDraft({});
    setGhostDirty(false);
    setGhostSaveError('');
  };

  // ── Unsaved-changes modal handlers (Phase 5) ──────────────────
  // The modal is shown when the user tries to navigate away from
  // a dirty ghost row (e.g. clicking Add Row again while the
  // current ghost has unsaved data). It resolves by calling one
  // of these three handlers, each of which clears unsavedPrompt
  // and then resumes or abandons the pending action.
  const resolveUnsavedSave = async () => {
    // Save the current ghost, then if the pending intent was
    // add-new-row, create a new one after the save completes.
    const pending = unsavedPrompt;
    setUnsavedPrompt(null);
    // Fire save; if it fails, the ghost stays and the user sees
    // ghostSaveError. Do NOT chain the pending action in that case.
    const beforeCount = rows.filter(r => isGhostRow(r.id)).length;
    await handleGhostSave();
    // handleGhostSave replaces the ghost with a real row on success.
    // We can only check success by re-reading ghostDirty from the
    // next render cycle, but since React state is async, defer via
    // setTimeout so the pending action fires against fresh state.
    setTimeout(() => {
      if (pending?.kind === 'add-new-row') {
        performAddRow();
      }
    }, 0);
    // (Other kinds deliberately do nothing further — the user is
    // now clear of the ghost and can pursue whatever they wanted.)
  };

  const resolveUnsavedDiscard = () => {
    const pending = unsavedPrompt;
    setUnsavedPrompt(null);
    handleGhostDiscard();
    if (pending?.kind === 'add-new-row') {
      // Discard clears state synchronously, so we can call straight.
      setTimeout(() => performAddRow(), 0);
    }
  };

  const resolveUnsavedCancel = () => {
    // Just close the modal, leave the ghost alone.
    setUnsavedPrompt(null);
  };

  // ── Column drag-to-reorder (owner/admin only) ───────────────
  // Uses native HTML5 DnD — no library dependency. Optimistic
  // update to the DbColumns array in local state, then persist
  // via PUT /columns/reorder; revert on failure.
  const handleColDragStart = (e, colId) => {
    // Block drag if a cell is being edited — dragging a column
    // header mid-edit would confuse the input focus and commit logic.
    if (editingCell) {
      e.preventDefault();
      return;
    }
    setDraggedColId(colId);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs setData to actually start the drag operation.
    e.dataTransfer.setData('text/plain', String(colId));
  };

  const handleColDragOver = (e, colId) => {
    if (draggedColId === null) return;
    e.preventDefault();  // required for drop to fire
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColId !== colId) setDragOverColId(colId);
  };

  const handleColDragLeave = () => {
    // No-op — we keep dragOverColId until a new target gets it or
    // the drag ends. Tracking every leave creates flicker.
  };

  const handleColDragEnd = () => {
    setDraggedColId(null);
    setDragOverColId(null);
  };

  const handleColDrop = async (e, targetColId) => {
    e.preventDefault();
    const srcId = draggedColId;
    setDraggedColId(null);
    setDragOverColId(null);

    if (!srcId || srcId === targetColId) return;
    if (!database?.DbColumns) return;

    // Compute the new order: remove src, insert it at target's index.
    const order = database.DbColumns.map(c => c.id);
    const srcIdx = order.indexOf(srcId);
    const tgtIdx = order.indexOf(targetColId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    order.splice(srcIdx, 1);
    order.splice(tgtIdx, 0, srcId);

    // Optimistic update — rebuild the column array in the new order
    // and bump each column's position so downstream code stays consistent.
    const byId = new Map(database.DbColumns.map(c => [c.id, c]));
    const newCols = order.map((id, i) => ({ ...byId.get(id), position: i }));
    const prevCols = database.DbColumns;
    setDatabase(prev => prev ? { ...prev, DbColumns: newCols } : prev);

    try {
      const res = await reorderColumns(org_slug, db_id, order);
      // Server returns the authoritative column list; use it to keep
      // any server-computed fields (e.g. updated_at) fresh.
      if (Array.isArray(res?.data)) {
        setDatabase(prev => prev ? { ...prev, DbColumns: res.data } : prev);
      }
    } catch (err) {
      // Revert on failure so the user doesn't see an inconsistent order.
      setDatabase(prev => prev ? { ...prev, DbColumns: prevCols } : prev);
      alert(err.response?.data?.error || 'Failed to reorder columns');
    }
  };

  const startEdit = (rowId, colId, currentValue) => {
    navigationRef.current = null;
    setEditingCell({ rowId, colId });
    setEditError('');  // fresh cell, fresh error state
    // For date columns, the native date picker requires YYYY-MM-DD
    // format in its value attribute. Convert from our stored
    // DD/MM/YYYY so the picker opens pre-populated correctly.
    const col = database?.DbColumns?.find(c => c.id === colId);
    const inputValue = (col?.data_type === 'date')
      ? toISO(currentValue || '')
      : (currentValue || '');
    setEditValue(inputValue);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = async (rowId, colId, value) => {
    const existingRow = rows.find(r => r.id === rowId);
    const isGhost = isGhostRow(rowId);

    // For date columns, the picker gives us YYYY-MM-DD but we store
    // DD/MM/YYYY. Convert before any further processing so the no-op
    // check, ghost promotion, and PUT all use the canonical format.
    const col = database?.DbColumns?.find(c => c.id === colId);
    const saveValue = (col?.data_type === 'date' && value)
      ? fromISO(value)
      : value;

    // ── Fill-then-save branch (Phase 3) ───────────────────────
    // User has can_add but not can_edit. Editing a ghost row must
    // not hit the network at all — instead accumulate the cell in
    // ghostDraft and mark the ghost dirty so later phases can
    // (a) surface a Save button, (b) confirm before navigation,
    // (c) warn on unload.
    //
    // This deliberately does not delete the ghost on empty blur
    // (unlike the legacy flow): in fill-then-save mode the user
    // often tabs through empty cells while building their row;
    // silently dropping the ghost mid-entry would be maddening.
    // The ghost is removed on explicit Discard (Phase 4).
    if (isGhost && fillThenSaveMode) {
      // ── Per-cell validate-on-blur ──
      // Time / phone / email columns: parse the user's entry, normalize
      // to canonical storage form, and reject unparseable input by
      // KEEPING the editor open with an inline error. Empty values pass
      // through unchanged (clearing a cell is always allowed).
      let valueToStore = saveValue;
      if (saveValue && (col?.data_type === 'number' || col?.data_type === 'currency' || col?.data_type === 'percentage')) {
        const n = Number(saveValue);
        if (isNaN(n) || saveValue.trim() === '') {
          setEditError('Enter a valid number');
          return false;
        }
        valueToStore = saveValue.trim();
      } else if (col?.data_type === 'time' && saveValue) {
        const mins = parseTimeToMinutes(saveValue);
        if (mins === null) {
          // Bad time input. Don't write to ghostDraft, don't clear
          // edit mode — let the user fix it before moving on.
          setEditError('Enter a valid time, e.g. 09:30 or 9:30 AM');
          return false;
        }
        valueToStore = formatMinutesToTime(mins);
      } else if (col?.data_type === 'hour' && saveValue) {
        const r = parseAndValidateHour(saveValue);
        if (!r.valid) {
          setEditError(r.error);
          return false;
        }
        valueToStore = r.value;
      } else if (col?.data_type === 'phone' && saveValue) {
        const r = parseAndValidatePhone(saveValue);
        if (!r.valid) {
          setEditError(r.error);
          return false;
        }
        valueToStore = r.value;
      } else if (col?.data_type === 'email' && saveValue) {
        const r = parseAndValidateEmail(saveValue);
        if (!r.valid) {
          setEditError(r.error);
          return false;
        }
        valueToStore = r.value;
      } else {
        // Any other type, OR an empty value of a validated type
        // (clearing is allowed) — clear stale error.
        setEditError('');
      }

      setGhostDraft(prev => ({ ...prev, [colId]: valueToStore ?? '' }));
      // Dirty flips true on any write, even writing back an empty
      // string — the user intentionally cleared a cell, which is
      // still a change they'd expect to save.
      setGhostDirty(true);
      // Clear the editor UI so the grid visually matches the
      // committed value (same as the normal flow).
      setEditingCell(null);
      setEditError('');
      // Don't set a save error; any earlier one stays until Save
      // is clicked or the user explicitly discards.
      return true;
    }

    // Ghost with no value entered: drop it silently. This is the
    // main anti-blank-row safety net.
    if (isGhost && (saveValue === '' || saveValue === null || saveValue === undefined)) {
      setRows(prev => prev.filter(r => r.id !== rowId));
      return;
    }

    // Real row with no-op edit: skip the PUT.
    if (!isGhost && saveValue === getCellValue(existingRow || {}, colId)) return;

    // ── Per-cell validate-on-blur for real rows ──
    // Same parity as the ghost-row branch above. If the user typed an
    // invalid phone / email / time, keep the editor open with an inline
    // error instead of round-tripping to the server and losing focus.
    // We use a local `valueForServer` so the rest of the function uses
    // the normalized value (digits-only phone, lowercased email,
    // 24h HH:MM time) — matching what the backend would have stored.
    let valueForServer = saveValue;
    if (saveValue && (col?.data_type === 'number' || col?.data_type === 'currency' || col?.data_type === 'percentage')) {
      const n = Number(saveValue);
      if (isNaN(n) || saveValue.trim() === '') {
        setEditError('Enter a valid number');
        return false;
      }
      valueForServer = saveValue.trim();
    } else if (col?.data_type === 'time' && saveValue) {
      const mins = parseTimeToMinutes(saveValue);
      if (mins === null) {
        setEditError('Enter a valid time, e.g. 09:30 or 9:30 AM');
        return false;
      }
      valueForServer = formatMinutesToTime(mins);
    } else if (col?.data_type === 'hour' && saveValue) {
      const r = parseAndValidateHour(saveValue);
      if (!r.valid) {
        setEditError(r.error);
        return false;
      }
      valueForServer = r.value;
    } else if (col?.data_type === 'phone' && saveValue) {
      const r = parseAndValidatePhone(saveValue);
      if (!r.valid) {
        setEditError(r.error);
        return false;
      }
      valueForServer = r.value;
    } else if (col?.data_type === 'email' && saveValue) {
      const r = parseAndValidateEmail(saveValue);
      if (!r.valid) {
        setEditError(r.error);
        return false;
      }
      valueForServer = r.value;
    } else {
      setEditError('');
    }

    // No-op check again, this time against the *normalized* value.
    // Saves a needless PUT when e.g. the user retyped '9:30 AM' over
    // a stored '09:30' — they're the same time, just different forms.
    if (!isGhost && valueForServer === getCellValue(existingRow || {}, colId)) return;

    setSaving(true);
    try {
      let res;
      if (isGhost) {
        // Promote the ghost: POST /rows with just the single cell the
        // user entered. Server returns a real row with a numeric id,
        // which replaces the ghost in state.
        res = await addRow(org_slug, db_id, {
          cells: { [colId]: valueForServer }
        });
        const real = res?.data;
        if (real && real.id) {
          setRows(prev => prev.map(row =>
            row.id === rowId ? real : row
          ));
        } else {
          // Server didn't return what we expected — drop the ghost
          // so the user doesn't see a stuck-looking row.
          setRows(prev => prev.filter(r => r.id !== rowId));
        }
      } else {
        res = await updateRow(org_slug, db_id, rowId, {
          cells: { [colId]: valueForServer }
        });
        const updated = res?.data;
        if (updated && updated.id === rowId) {
          setRows(prev => prev.map(row => row.id === rowId ? updated : row));
        } else {
          // Fallback: server returned something unexpected. Patch
          // the changed cell in place so at least the user's edit sticks.
          setRows(prev => prev.map(row => {
            if (row.id !== rowId) return row;
            const cells = row.DbCells ? [...row.DbCells] : [];
            const idx = cells.findIndex(c => c.column_id === colId);
            if (idx >= 0) cells[idx] = { ...cells[idx], value: valueForServer };
            else cells.push({ column_id: colId, value: valueForServer });
            return { ...row, DbCells: cells };
          }));
        }
      }
    } catch (err) {
      // If a ghost POST failed, drop the ghost so we don't show a
      // permanent dead row the user can't recover from.
      if (isGhost) {
        setRows(prev => prev.filter(r => r.id !== rowId));
      }
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, rowId, colId) => {
    const cols = database?.DbColumns || [];
    const colIdx = cols.findIndex(c => c.id === colId);
    const currentRows = rows.filter(row =>
      isGhostRow(row.id) || matchesSearch(row, search)
    );
    const rowIdx = currentRows.findIndex(r => r.id === rowId);

    if (e.key === 'Escape') {
      e.preventDefault();
      navigationRef.current = 'escape';
      setEditingCell(null);
      setEditValue('');
      // If the user was editing a ghost row and bailed out, drop it.
      if (isGhostRow(rowId)) dropGhostRows();
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      navigationRef.current = 'tab';
      const val = editValueRef.current;

      // Find next editable column (skip formula columns — they are
      // computed, not entered). Also skip PDF columns since they
      // are handled by PdfCell, not the generic edit path.
      const isEditable = (c) => c.data_type !== 'formula' && c.data_type !== 'pdf';
      const findNextEditableCol = (startIdx) => {
        for (let i = startIdx; i < cols.length; i++) {
          if (isEditable(cols[i])) return i;
        }
        return -1;
      };

      commitEdit(rowId, colId, val).then(committed => {
        if (committed === false) {
          // Validation blocked — stay on this cell. Re-focus the
          // input so the user can edit immediately.
          setTimeout(() => inputRef.current?.focus(), 0);
          return;
        }
        const nextInRow = findNextEditableCol(colIdx + 1);
        if (nextInRow !== -1) {
          startEdit(rowId, cols[nextInRow].id,
            getCellValue(currentRows[rowIdx], cols[nextInRow].id));
        } else if (rowIdx < currentRows.length - 1) {
          const firstInNextRow = findNextEditableCol(0);
          if (firstInNextRow !== -1) {
            startEdit(currentRows[rowIdx + 1].id, cols[firstInNextRow].id,
              getCellValue(currentRows[rowIdx + 1], cols[firstInNextRow].id));
          } else {
            setEditingCell(null);
          }
        } else {
          setEditingCell(null);
        }
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      navigationRef.current = 'enter';
      const val = editValueRef.current;

      commitEdit(rowId, colId, val).then(committed => {
        if (committed === false) {
          setTimeout(() => inputRef.current?.focus(), 0);
          return;
        }
        if (rowIdx < currentRows.length - 1) {
          startEdit(currentRows[rowIdx + 1].id, colId,
            getCellValue(currentRows[rowIdx + 1], colId));
        } else {
          setEditingCell(null);
        }
      });
    }
  };

  const handleBlur = (rowId, colId) => {
    // Only save on blur if NOT navigating via keyboard
    if (navigationRef.current) {
      navigationRef.current = null;
      return;
    }
    // commitEdit returns false when it blocks the commit (e.g. inline
    // validation failed). In that case, leave the editor open and
    // focused so the user can fix it; clearing editingCell here would
    // dismiss the input even though the value was rejected.
    const result = commitEdit(rowId, colId, editValueRef.current);
    Promise.resolve(result).then(committed => {
      if (committed === false) {
        // Re-focus the input so blur->click->blur loops don't trap
        // the user in a half-state. Setting editingCell again is a
        // no-op (already set), but the focus call rehydrates focus.
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
      setEditingCell(null);
      setEditValue('');
    });
  };

  // ── Cell Action History (Clause 5.3) ─────────────────────────
  // Right-click on a cell fetches and displays the change history
  // for that specific cell in a floating popover.
  const handleContextMenu = async (e, rowId, col) => {
    // Don't interfere when the user is actively editing — they may
    // want the browser's native paste/cut/copy menu in the input.
    if (editingCell?.rowId === rowId && editingCell?.colId === col.id) return;

    e.preventDefault();

    // Position the popover near the cursor but keep it on-screen.
    const POPOVER_W = 360;
    const POPOVER_H = 400;
    const pad = 8;
    let x = e.clientX;
    let y = e.clientY;
    if (x + POPOVER_W > window.innerWidth  - pad) x = window.innerWidth  - POPOVER_W - pad;
    if (y + POPOVER_H > window.innerHeight - pad) y = window.innerHeight - POPOVER_H - pad;
    if (x < pad) x = pad;
    if (y < pad) y = pad;

    setHistory({
      anchorX: x, anchorY: y,
      rowId, colId: col.id,
      columnLabel: col.label,
      loading: true,
      events: [],
      isFormula: col.data_type === 'formula',
      error: null
    });

    // Formula columns are computed: no history to fetch.
    if (col.data_type === 'formula') {
      setHistory(prev => prev ? { ...prev, loading: false } : null);
      return;
    }

    try {
      const res = await getCellHistory(org_slug, db_id, rowId, col.id);
      setHistory(prev => prev && prev.rowId === rowId && prev.colId === col.id
        ? { ...prev, loading: false, events: res.data.events || [] }
        : prev);
    } catch (err) {
      setHistory(prev => prev && prev.rowId === rowId && prev.colId === col.id
        ? { ...prev, loading: false, error: err.response?.data?.error || 'Failed to load history' }
        : prev);
    }
  };

  const closeHistory = () => setHistory(null);

  // Close popover on Esc or click outside
  useEffect(() => {
    if (!history) return;
    const onKey = (e) => { if (e.key === 'Escape') closeHistory(); };
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        closeHistory();
      }
    };
    window.addEventListener('keydown', onKey);
    // mousedown not click, so a right-click elsewhere also dismisses
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [history]);

  // PDF upload/view/delete handlers are gone as of v1.8 — they
  // were the row-level "single PDF per row" surface. PDFs are
  // now per-cell, handled inline by the <PdfCell> component
  // declared above DatabaseView. The component calls
  // uploadPdfToCell / deletePdfFromCell / getPdfCellUrl directly
  // and refreshes via its `onChange` callback (wired to loadData
  // below in the cell render).

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `/api/${org_slug}/databases/${db_id}/export`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${database?.db_number}-${database?.name}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed');
    }
  };

  const handleCopyLink = async () => {
    try {
      const res = await generateLink(org_slug, db_id);
      await navigator.clipboard.writeText(res.data.link);
      setCopyMsg('Link copied!');
      setTimeout(() => setCopyMsg(''), 3000);
    } catch (err) {
      alert('Failed to generate link');
    }
  };

  // Revoke any active public link for this database. After revoke,
  // the public URL stops serving content (returns 403) and existing
  // copies of the link become useless. Audited as LINK_REVOKED on
  // success, and any subsequent public access attempts are
  // captured as LINK_ACCESS_DENIED for forensics.
  const handleRevokeLink = async () => {
    if (!window.confirm(
      'Revoke the public link for this database?\n\n' +
      'Anyone holding the current link will lose access immediately. ' +
      'You can generate a new link any time.'
    )) return;
    try {
      await revokeLink(org_slug, db_id);
      setCopyMsg('Link revoked');
      setTimeout(() => setCopyMsg(''), 3000);
    } catch (err) {
      // Backend returns 404 when no active link exists. Treat that
      // as a friendly outcome ("nothing to revoke") rather than an
      // error, since the user's intent — "make sure no public link
      // is active" — is already satisfied.
      const status = err?.response?.status;
      if (status === 404) {
        setCopyMsg('No active link to revoke');
        setTimeout(() => setCopyMsg(''), 3000);
        return;
      }
      alert('Failed to revoke link');
    }
  };

  const filteredRows = rows.filter(row =>
    isGhostRow(row.id) || matchesSearch(row, search)
  );

  if (loading) return (
    <div style={styles.center}>Loading database...</div>
  );

  if (error) return (
    <div style={styles.center}>
      <p style={{ color: '#C0392B', marginBottom: '16px' }}>{error}</p>
      <button className="btn btn-secondary" onClick={() => navigate(-1)}>
        Go Back
      </button>
    </div>
  );

  const cols = database?.DbColumns || [];

  // Totals row (CEO Clause 7.1.2): compute sum for each currency /
  // number / formula column. When user has filtered, show both the
  // overall total AND the filtered subtotal so they can see "of
  // the 500 rows of sales, this filter covers 87k of the 450k".
  // Ghost (pending-add) rows are excluded — they have no real data.
  const hasSummableColumn = cols.some(c => SUMMABLE_TYPES.includes(c.data_type));
  const realRows = rows.filter(r => !isGhostRow(r.id));
  const realFilteredRows = filteredRows.filter(r => !isGhostRow(r.id));
  const totalsAll = hasSummableColumn ? computeTotals(realRows, cols) : {};
  const totalsFiltered = hasSummableColumn && search
    ? computeTotals(realFilteredRows, cols)
    : null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.dbNumber}>{database?.db_number}</div>
          {renaming ? (
            <div style={styles.renameRow}>
              <input
                ref={renameInputRef}
                type="text"
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={handleRenameKey}
                onBlur={commitRename}
                disabled={renameSaving}
                maxLength={255}
                style={styles.renameInput}
              />
              {renameSaving && <span style={styles.renameHint}>saving…</span>}
            </div>
          ) : (
            <h1
              style={{ ...styles.title, ...(isOwner ? styles.titleEditable : {}) }}
              onClick={startRename}
              title={isOwner ? 'Click to rename' : undefined}
            >
              {database?.name}
              {isOwner && <span style={styles.renamePencil}>✎</span>}
            </h1>
          )}
          {database?.owner && (
            <p style={styles.ownerMeta}>
              👤 <strong>{database.owner.name}</strong>
              <span style={{ color: '#aaa', margin: '0 4px' }}>·</span>
              <span style={{ color: '#888' }}>{database.owner.email}</span>
            </p>
          )}
          <p style={styles.meta}>
            {cols.length} columns &nbsp;·&nbsp; {rows.length} rows
            {saving && <span style={styles.saving}> · saving...</span>}
          </p>
        </div>
        <div style={styles.headerActions}>
          {copyMsg && <span style={styles.copyMsg}>{copyMsg}</span>}
          <button className="btn btn-secondary"
            onClick={handleExport}
            style={{ fontSize: '13px' }}>
            ⬇ Export
          </button>
          {isOwner && (
            <button className="btn btn-secondary"
              onClick={() => navigate(`/${org_slug}/databases/${db_id}/columns`)}
              style={{ fontSize: '13px' }}>
              ⚙ Columns
            </button>
          )}
          {isOwner && (
            <button className="btn btn-secondary"
              onClick={() => navigate(`/${org_slug}/databases/${db_id}/permissions`)}
              style={{ fontSize: '13px' }}>
              👥 Access
            </button>
          )}
          {isOwner && (
            <button className="btn btn-secondary"
              onClick={handleCopyLink} style={{ fontSize: '13px' }}>
              🔗 Copy Link
            </button>
          )}
          {isOwner && (
            <button className="btn btn-secondary"
              onClick={handleRevokeLink}
              style={{ fontSize: '13px', color: '#C0392B' }}
              title="Revoke any active public link for this database">
              🚫 Revoke Link
            </button>
          )}
          <button className="btn btn-primary"
            onClick={handleAddRow} style={{ fontSize: '13px' }}>
            + Add Row
          </button>
        </div>
      </div>

      <input
        type="text"
        placeholder="🔍  Search — space-separated words, all must match (e.g. priya mumbai)"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={styles.search}
      />

      <div style={styles.gridWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.thNum}>#</th>
              {cols.map(col => {
                // Visual cues:
                //  - the column currently being dragged -> reduced opacity
                //  - the column under the cursor (drop target) -> left border highlight
                const isDragged = draggedColId === col.id;
                const isDropTarget = dragOverColId === col.id && draggedColId !== null && draggedColId !== col.id;
                return (
                  <th
                    key={col.id}
                    style={{
                      ...styles.th,
                      opacity: isDragged ? 0.4 : 1,
                      cursor: isOwner ? 'grab' : 'default',
                      ...(isDropTarget ? styles.thDropTarget : {})
                    }}
                    draggable={isOwner}
                    onDragStart={e => handleColDragStart(e, col.id)}
                    onDragOver={e => handleColDragOver(e, col.id)}
                    onDragLeave={handleColDragLeave}
                    onDrop={e => handleColDrop(e, col.id)}
                    onDragEnd={handleColDragEnd}
                    title={isOwner ? 'Drag to reorder' : undefined}
                  >
                    {col.label}
                    <span style={styles.typeTag}>{col.data_type}</span>
                  </th>
                );
              })}
              <th style={styles.thAction}></th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={cols.length + 1} style={styles.emptyRow}>
                  {search ? 'No results found'
                    : 'No rows yet — click + Add Row to start'}
                </td>
              </tr>
            ) : (
              filteredRows.map((row, idx) => (
                <tr key={row.id} style={{
                  ...styles.tr,
                  background: idx % 2 === 0 ? 'white' : '#fafafa'
                }}>
                  <td style={styles.tdNum}>{idx + 1}</td>
                  {cols.map(col => {
                    // ── PDF columns render via <PdfCell> ─────
                    // Short-circuits the generic editing path entirely:
                    // no startEdit on click, no outline-on-edit, no
                    // right-click history (the audit log still records
                    // PDF uploads/deletes via PDF_UPLOAD / PDF_REPLACE /
                    // PDF_DELETE, addressable through the row-level
                    // audit view). PdfCell handles its own
                    // click/drag/drop and stops propagation so it
                    // doesn't trigger the surrounding td's handlers.
                    if (col.data_type === 'pdf') {
                      // Ghost rows don't have a server-side ID yet, so
                      // the PDF upload endpoint (which needs rowId) would
                      // 404 "Row not found". Render a non-interactive
                      // placeholder instead and let the user save the
                      // row first. Once saved, the row is replaced with
                      // a real row and this branch re-renders PdfCell
                      // normally. (Apr 25 — ghost-row PDF fix.)
                      if (isGhostRow(row.id) && fillThenSaveMode) {
                        return (
                          <td key={col.id} style={{ ...styles.td, padding: 0 }}>
                            <div style={styles.pdfGhostPlaceholder}
                                 title="Save the row first, then you can attach a PDF">
                              Save row first to attach PDF
                            </div>
                          </td>
                        );
                      }
                      const file = (row.DbCellFiles || [])
                        .find(f => f.column_id === col.id) || null;
                      // Permission: mirrors the backend's upload gate.
                      // isOwner already folds in platform_admin /
                      // super_admin (see computation at top of
                      // component). Regular users still pass through
                      // — the backend 403s if they lack
                      // can_upload_pdf / can_delete_pdf, and the
                      // PdfCell surfaces that error inline. This
                      // matches the pre-v1.8 behavior where everyone
                      // saw the upload icon and the backend gated it.
                      return (
                        <td key={col.id} style={{ ...styles.td, padding: 0 }}>
                          <PdfCell
                            orgSlug={org_slug}
                            databaseId={db_id}
                            rowId={row.id}
                            columnId={col.id}
                            file={file}
                            canUpload={true}
                            canDelete={true}
                            onChange={loadData}
                          />
                        </td>
                      );
                    }

                    const isEditing = editingCell?.rowId === row.id &&
                      editingCell?.colId === col.id;
                    const val = getCellValue(row, col.id);
                    return (
                      <td key={col.id} style={{
                        ...styles.td,
                        background: isEditing ? '#EBF3FB' : 'inherit',
                        outline: isEditing ? '2px solid #2E75B6' : 'none',
                        outlineOffset: '-2px'
                      }}
                        onClick={() => !isEditing && col.data_type !== 'formula' && startEdit(row.id, col.id, val)}
                        onContextMenu={(e) => handleContextMenu(e, row.id, col)}
                      >
                        {isEditing ? (
                          (() => {
                            const dropdownOptions = getAllowedValues(col);
                            if (dropdownOptions) {
                              // Dropdown-restricted cell: render <select>.
                              // Keep the same Tab/Enter/Esc navigation wired up.
                              return (
                                <select
                                  ref={inputRef}
                                  value={editValue}
                                  onChange={e => {
                                    editValueRef.current = e.target.value;
                                    setEditValue(e.target.value);
                                  }}
                                  onBlur={() => handleBlur(row.id, col.id)}
                                  onKeyDown={e => handleKeyDown(e, row.id, col.id)}
                                  style={styles.cellInput}
                                >
                                  <option value="">— select —</option>
                                  {dropdownOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <>
                                <input
                                  ref={inputRef}
                                  value={editValue}
                                  onChange={e => {
                                    // Update both state AND the ref synchronously.
                                    // The ref is what handleBlur reads on blur; if we
                                    // only update state, the useEffect sync runs after
                                    // onBlur and the date picker's value is lost.
                                    editValueRef.current = e.target.value;
                                    setEditValue(e.target.value);
                                    // Clear stale error as the user keeps typing.
                                    if (editError) setEditError('');
                                  }}
                                  onBlur={() => handleBlur(row.id, col.id)}
                                  onKeyDown={e => handleKeyDown(e, row.id, col.id)}
                                  style={{
                                    ...styles.cellInput,
                                    ...(editError ? { borderColor: '#C0392B', outline: '2px solid #C0392B' } : {}),
                                    ...(col.data_type === 'date'
                                      ? { colorScheme: 'light', cursor: 'pointer' }
                                      : {})
                                  }}
                                  type={
                                    col.data_type === 'date' ? 'date' : 'text'
                                  }
                                  inputMode={
                                    col.data_type === 'number' ||
                                    col.data_type === 'currency' ||
                                    col.data_type === 'percentage' ? 'decimal' : undefined
                                  }
                                  placeholder={
                                    col.data_type === 'hour' ? 'H:MM' : undefined
                                  }
                                  min={col.data_type === 'date' ? '1900-01-01' : undefined}
                                  max={col.data_type === 'date' ? '2100-12-31' : undefined}
                                />
                                {editError && (
                                  <div style={styles.cellEditError}>{editError}</div>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <span style={styles.cellValue}>
                            {val
                              ? (col.data_type === 'time' ? formatTimeForDisplay(val)
                                : col.data_type === 'hour' ? formatHourForDisplay(val)
                                : val)
                              : <span style={styles.placeholder}>click to edit</span>}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td style={styles.tdAction}>
                    {isGhostRow(row.id) && fillThenSaveMode ? (
                      <div style={styles.ghostActions}>
                        <button
                          tabIndex={-1}
                          onClick={handleGhostSave}
                          disabled={saving}
                          style={styles.ghostSaveBtn}
                        >{saving ? '…' : 'Save'}</button>
                        <button
                          tabIndex={-1}
                          onClick={handleGhostDiscard}
                          disabled={saving}
                          style={styles.ghostDiscardBtn}
                        >Discard</button>
                        {ghostSaveError && (
                          <div style={styles.ghostError}>{ghostSaveError}</div>
                        )}
                      </div>
                    ) : (
                      <button
                        tabIndex={-1}
                        onClick={() => handleDeleteRow(row.id)}
                        style={styles.deleteBtn}
                      >🗑</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {hasSummableColumn && realFilteredRows.length > 0 && (
            <tfoot>
              <tr style={styles.totalsRow}>
                <td style={styles.totalsLabel}>Total</td>
                {cols.map(col => {
                  const summable = SUMMABLE_TYPES.includes(col.data_type);
                  if (!summable) {
                    return <td key={col.id} style={styles.totalsCell}></td>;
                  }
                  const allVal = totalsAll[col.id];
                  const filtVal = totalsFiltered ? totalsFiltered[col.id] : null;
                  if (allVal === null || allVal === undefined) {
                    return <td key={col.id} style={styles.totalsCell}></td>;
                  }
                  return (
                    <td key={col.id} style={styles.totalsCell}>
                      {totalsFiltered !== null ? (
                        // Search active — show both overall and filtered
                        <div style={styles.totalsStack}>
                          <div style={styles.totalsMain}>{typeof allVal === "object" && allVal?.type === "time" ? formatMinuteSum(allVal.value) : formatSum(allVal)}</div>
                          <div style={styles.totalsSub}>
                            Filtered: {typeof filtVal === "object" && filtVal?.type === "time" ? formatMinuteSum(filtVal.value) : formatSum(filtVal)}
                          </div>
                        </div>
                      ) : (
                        <div style={styles.totalsMain}>{typeof allVal === "object" && allVal?.type === "time" ? formatMinuteSum(allVal.value) : formatSum(allVal)}</div>
                      )}
                    </td>
                  );
                })}
                {/* One empty cell to cover the action (delete-row) column */}
                <td style={styles.totalsCell}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div style={styles.footer}>
        <span style={{ color: '#595959', fontSize: '12px' }}>
          {realFilteredRows.length} of {realRows.length} rows
          {search && ' (filtered)'}
        </span>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', color: '#aaa' }}>
            Tab → next cell &nbsp;·&nbsp; Enter → row below &nbsp;·&nbsp; Esc → cancel
          </span>
          {isOwner && (
            <button className="btn btn-secondary"
              onClick={() => navigate(`/${org_slug}/databases/${db_id}/audit`)}
              style={{ fontSize: '12px', padding: '4px 10px' }}>
              📋 Audit Log
            </button>
          )}
        </div>
      </div>

      {history && (
        <CellHistoryPopover
          ref={popoverRef}
          state={history}
          onClose={closeHistory}
        />
      )}

      {/* Unsaved-changes modal (Phase 5). Appears when a ghost row
          has user data and the user triggers a navigation that
          would otherwise wipe it (e.g. Add Row while dirty).
          Save / Discard / Cancel. */}
      {unsavedPrompt && (
        <div style={styles.modalOverlay} onClick={resolveUnsavedCancel}>
          <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Unsaved changes</h3>
            <p style={styles.modalBody}>
              You have unsaved data in a new row. Save it, discard it,
              or stay on the current row to keep editing.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={resolveUnsavedSave}
                disabled={saving}
                style={styles.modalSaveBtn}
              >{saving ? '…' : 'Save'}</button>
              <button
                onClick={resolveUnsavedDiscard}
                disabled={saving}
                style={styles.modalDiscardBtn}
              >Discard</button>
              <button
                onClick={resolveUnsavedCancel}
                disabled={saving}
                style={styles.modalCancelBtn}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Cell History Popover ────────────────────────────────────────
// Floating panel anchored near the right-clicked cell. Shows the
// complete edit trail for that single cell, newest-first.
// Styled to match the audit log sentence format.
// (MONTHS array defined at top of file — reused here)
const formatCellDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ` +
         `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const q = (v) => (v === null || v === undefined || v === '') ? '—' : `'${String(v)}'`;

const CellHistoryPopover = React.forwardRef(({ state, onClose }, ref) => {
  const { anchorX, anchorY, columnLabel, loading, events, isFormula, error } = state;

  const renderEvent = (ev) => {
    const who = ev.user?.email || 'system';
    const when = formatCellDate(ev.created_at);
    switch (ev.type) {
      case 'insert':
        return (
          <>
            <strong>Created</strong> with value {q(ev.value)}
            <div style={popStyles.eventMeta}>on {when} by {who}</div>
          </>
        );
      case 'update':
        return (
          <>
            <strong>Updated</strong> from {q(ev.before)} to {q(ev.after)}
            <div style={popStyles.eventMeta}>on {when} by {who}</div>
          </>
        );
      case 'delete':
        return (
          <>
            <strong>Row removed</strong> (value was {q(ev.value)})
            <div style={popStyles.eventMeta}>on {when} by {who}</div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      style={{
        ...popStyles.popover,
        left: anchorX,
        top: anchorY
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div style={popStyles.header}>
        <div style={popStyles.title}>
          History — <strong>{columnLabel}</strong>
        </div>
        <button onClick={onClose} style={popStyles.closeBtn} title="Close (Esc)">
          ✕
        </button>
      </div>
      <div style={popStyles.body}>
        {loading && <div style={popStyles.message}>Loading…</div>}
        {!loading && error && <div style={popStyles.error}>{error}</div>}
        {!loading && !error && isFormula && (
          <div style={popStyles.message}>
            This is a formula column. History is not tracked for computed values.
          </div>
        )}
        {!loading && !error && !isFormula && events.length === 0 && (
          <div style={popStyles.message}>No history for this cell.</div>
        )}
        {!loading && !error && !isFormula && events.length > 0 && (
          <ul style={popStyles.eventList}>
            {events.map((ev, i) => (
              <li key={i} style={popStyles.eventItem}>
                {renderEvent(ev)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
});

const popStyles = {
  popover: {
    position: 'fixed',
    width: '360px',
    maxHeight: '400px',
    background: 'white',
    border: '1px solid #d0d0d0',
    borderRadius: '8px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fafafa'
  },
  title: { fontSize: '13px', color: '#1B3A5C' },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: '#595959', padding: '0 4px'
  },
  body: {
    overflowY: 'auto',
    padding: '8px 0'
  },
  message: { padding: '16px', fontSize: '13px', color: '#595959', textAlign: 'center' },
  error:   { padding: '16px', fontSize: '13px', color: '#C0392B', textAlign: 'center' },
  eventList: { listStyle: 'none', margin: 0, padding: 0 },
  eventItem: {
    padding: '10px 14px',
    borderBottom: '1px solid #f5f5f5',
    fontSize: '13px',
    color: '#1B3A5C',
    lineHeight: '1.5'
  },
  eventMeta: {
    marginTop: '2px',
    fontSize: '11px',
    color: '#595959'
  }
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', fontSize: '16px', color: '#2E75B6' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  dbNumber: { fontSize: '11px', color: '#2E75B6', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '4px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#1B3A5C' },
  titleEditable: { cursor: 'pointer', borderRadius: '4px', padding: '2px 6px', margin: '0 -6px', transition: 'background 0.15s' },
  renamePencil: { marginLeft: '8px', fontSize: '13px', color: '#2E75B6', opacity: 0.6 },
  renameRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  renameInput: { fontSize: '22px', fontWeight: '700', color: '#1B3A5C', border: '2px solid #2E75B6', borderRadius: '4px', padding: '2px 8px', outline: 'none', minWidth: '320px', background: '#fff' },
  renameHint: { fontSize: '12px', color: '#B7600A', fontStyle: 'italic' },
  meta: { fontSize: '13px', color: '#595959', marginTop: '2px' },
  ownerMeta: { fontSize: '12px', color: '#595959', marginTop: '2px', marginBottom: '0' },
  saving: { color: '#B7600A', fontStyle: 'italic' },
  headerActions: { display: 'flex', gap: '8px', alignItems: 'center' },
  copyMsg: { color: '#1E7B34', fontSize: '13px', fontWeight: '500' },
  search: { width: '100%', padding: '8px 14px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', outline: 'none', marginBottom: '12px' },
  gridWrapper: { flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px', background: 'white' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  thNum: { width: '50px', padding: '10px 8px', background: '#1B3A5C', color: 'white', fontWeight: '600', textAlign: 'center', fontSize: '12px', position: 'sticky', top: 0, zIndex: 1 },
  th: { padding: '10px 12px', background: '#1B3A5C', color: 'white', fontWeight: '600', textAlign: 'left', fontSize: '13px', borderLeft: '1px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' },
  thAction: { width: '50px', background: '#1B3A5C', position: 'sticky', top: 0, zIndex: 1 },
  thDropTarget: { borderLeft: '3px solid #FFD966' },
  typeTag: { marginLeft: '6px', fontSize: '10px', opacity: 0.6, fontWeight: '400', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  tdNum: { padding: '8px', textAlign: 'center', color: '#595959', fontSize: '12px', background: '#fafafa', borderRight: '1px solid #f0f0f0' },
  td: { padding: '4px 8px', borderLeft: '1px solid #f0f0f0', cursor: 'pointer', minWidth: '120px', maxWidth: '300px' },
  tdAction: { padding: '4px 8px', textAlign: 'center', borderLeft: '1px solid #f0f0f0', width: '50px' },
  cellInput: { width: '100%', padding: '4px 6px', border: 'none', background: 'transparent', fontSize: '13px', outline: 'none', color: '#1B3A5C' },
  cellValue: { display: 'block', padding: '4px 4px', fontSize: '13px', color: '#1B3A5C', minHeight: '24px' },
  placeholder: { color: '#ccc', fontSize: '12px', fontStyle: 'italic' },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px', opacity: 0.5 },

  // Fill-then-save (Phase 4): Save + Discard + inline error shown in
  // place of the trash icon when the row is a ghost in fill-then-save
  // mode. Vertical stack so the inline error can wrap onto its own
  // row under the buttons if the server returns a long message.
  ghostActions: { display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' },
  ghostSaveBtn: {
    padding: '3px 10px', fontSize: '11px', fontWeight: 600,
    background: '#2E75B6', color: '#fff', border: 'none',
    borderRadius: '3px', cursor: 'pointer', lineHeight: 1.2
  },
  ghostDiscardBtn: {
    padding: '3px 8px', fontSize: '11px',
    background: '#f0f0f0', color: '#595959', border: 'none',
    borderRadius: '3px', cursor: 'pointer', lineHeight: 1.2
  },
  ghostError: {
    fontSize: '11px', color: '#C0392B', maxWidth: '260px',
    textAlign: 'left', marginTop: '4px',
    whiteSpace: 'pre-line',  // honour \n from the error formatter
    lineHeight: '1.35'
  },
  // ── PDF placeholder for ghost rows (Apr 25) ──
  // Ghost rows have no server-side ID; PDF uploads need one. Show a
  // muted informational line instead of the upload button so users
  // know to save the row first. Not clickable, not red (not an
  // error), just a gentle nudge.
  pdfGhostPlaceholder: {
    padding: '6px 8px',
    fontSize: '11px',
    color: '#9c9c9c',
    fontStyle: 'italic',
    textAlign: 'center',
    lineHeight: '1.3',
    cursor: 'default',
    userSelect: 'none'
  },
  // Per-cell validation error shown beneath the editing input.
  // (Apr 25 — time-on-blur for ghost rows.)
  cellEditError: {
    fontSize: '11px',
    color: '#C0392B',
    marginTop: '2px',
    lineHeight: '1.3',
    whiteSpace: 'normal'
  },

  // Unsaved-changes modal (Phase 5)
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000
  },
  modalBox: {
    background: 'white', borderRadius: '8px', padding: '22px 26px',
    maxWidth: '420px', width: '90%',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
  },
  modalTitle: {
    margin: 0, marginBottom: '10px',
    fontSize: '16px', fontWeight: 700, color: '#1B3A5C'
  },
  modalBody: {
    margin: 0, marginBottom: '18px',
    fontSize: '13px', color: '#595959', lineHeight: 1.5
  },
  modalActions: {
    display: 'flex', justifyContent: 'flex-end', gap: '8px'
  },
  modalSaveBtn: {
    padding: '6px 16px', fontSize: '13px', fontWeight: 600,
    background: '#2E75B6', color: '#fff', border: 'none',
    borderRadius: '4px', cursor: 'pointer'
  },
  modalDiscardBtn: {
    padding: '6px 14px', fontSize: '13px',
    background: '#fff', color: '#C0392B',
    border: '1px solid #E7B4AE', borderRadius: '4px', cursor: 'pointer'
  },
  modalCancelBtn: {
    padding: '6px 14px', fontSize: '13px',
    background: '#f0f0f0', color: '#595959',
    border: 'none', borderRadius: '4px', cursor: 'pointer'
  },
  emptyRow: { textAlign: 'center', padding: '40px', color: '#595959', fontSize: '14px' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '8px' },

  // Totals row (Clause 7.1.2)
  totalsRow: { background: '#EBF3FB', borderTop: '2px solid #1B3A5C' },
  totalsLabel: {
    padding: '10px 8px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#1B3A5C',
    background: '#D6E4F0',
    borderRight: '1px solid #c0d0e0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  totalsCell: {
    padding: '8px 12px',
    borderLeft: '1px solid #c0d0e0',
    fontSize: '13px',
    color: '#1B3A5C',
    textAlign: 'right',
    fontWeight: '600'
  },
  totalsStack: { display: 'flex', flexDirection: 'column', gap: '2px' },
  totalsMain: { fontWeight: '700' },
  totalsSub:  { fontSize: '11px', fontWeight: '500', color: '#595959' }
};

export default DatabaseView;
